import FullScreenDialog, { FullScreenDialogCloseReason } from "../../../components/full-screen-dialog/FullScreenDialog";
import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../../hooks/use-dialog-state/UseDialogState";
import Form from "../../forms/form/Form";
import LoadingZone from "../../loading-zone/LoadingZone";
import MobileStepMenu from "../../step-menu/mobile/MobileStepMenu";
import TierEditorUtils, { defaultEditTier, StepEnum, TierAdminMode, TierFormType, tierNotificationActionItems, TierNotificationActions } from "./TierEditorUtils";
import StepMenu, { StepDefinition } from "../../step-menu/StepMenu";
import UnsavedChangesPrompt from "../../unsaved-changes-prompt/UnsavedChangesPrompt";
import { useState, useRef, useMemo, useCallback, useEffect, type JSX } from "react";
import { isMobile, isTablet } from "react-device-detect";
import { useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useConfirmation } from "../../../contexts/confirmation-context/ConfirmationContext";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { ActionMenuItem, BaseActions } from "../../action-menu/ActionMenuItem";
import { ViewOptionStateEnum } from "../../contextual-app-bar/ViewOptionStateEnum";
import { useStepMenuState, StepMenuEditModeEnum } from "../../step-menu/StepMenuState";
import { useAlarmSchemeReferenceData } from "../../../queries/alarm-scheme-admin/UseAlarmSchemeReferenceData";
import TierSettings from "./tier-settings/TierSettings";
import { AlarmNotificationTypeEnum } from "../../../common/enums/AlarmNotificationTypeEnum";
import { AlarmSchemeTierType } from "../../alarm-scheme-tier-card/AlarmSchemeTierUtils";
import TierNotifications, { TierNotificationsType } from "./tier-notifications/TierNotifications";
import { ALWAYS_SCHEDULE, IEditTierDTO } from "../../../services/alarm-scheme-data-service/Models";

export type TierEditorDialogInitValues = {
    groupID: number,
    organizationID: number,
    item: AlarmSchemeTierType,
    tiers: IEditTierDTO[]
};

export type TierEditorDialogResultValues = {
    tier: TierFormType | undefined;
};

export type TierEditorDialogResultReason = FullScreenDialogCloseReason | "cancel" | "save";

export type TierEditorDialogResult = DialogResult<TierEditorDialogResultValues, TierEditorDialogResultReason>;

export type TierEditorDialogProps = DialogProps<TierEditorDialogInitValues, TierEditorDialogResult>;

export default function TierEditorDialog({
    open,
    onClose,
    values
}: TierEditorDialogProps): JSX.Element {


    const isPhone = isMobile && !isTablet;
    const { confirm
        //, acknowledge 
    } = useConfirmation();
    const { t } = useTranslation();
    const [viewOptionState, setViewOptionState] = useState(ViewOptionStateEnum.Default);
    const [openSideSheet, setOpenSideSheet] = useState(isPhone ? false: true);
    
    const tierNotificationsRef = useRef<TierNotificationsType>(undefined);

    const mode = (values?.item.id ?? 0) <= 0 ? 
        TierAdminMode.Add : 
        TierAdminMode.Edit;


    const form = useForm<TierFormType>({
        //mode: "all",
        mode: 'onChange',
        defaultValues: {...defaultEditTier,
        }
    });

    const {
        getValues,
        //watch,
        formState: {
            dirtyFields,
            //isDirty,
            isValid,
            //defaultValues
            //touchedFields
        },
        reset,
        //setValue,
        control,
        handleSubmit,
    } = form;
    
    const hasChanges = Object.keys(dirtyFields).length > 0 && isValid;


    //console.log(`Tier Editor ${isDirty} and ${hasChanges}`);

    const tierForm = useWatch({ control: control }) as TierFormType;


    const {data: referenceData, status: referenceStatus} = useQueryNotifications(useAlarmSchemeReferenceData)({
        groupID: values?.groupID as number ,
        //organizationID: group?.organizationID ?? -1,xxxx
        organizationID: values?.organizationID as number,
        enabled: values?.groupID !== undefined
    });

    const availableSchedules = referenceData?.schedules.filter(o => o.id !== ALWAYS_SCHEDULE) ?? [];

    const settingsValid = tierForm.name !== '' && isValid &&
        (tierForm.type === AlarmNotificationTypeEnum.Once || 
            (tierForm.type === AlarmNotificationTypeEnum.EveryXMinutes && tierForm.repeatMinutes !== undefined) ||
            (tierForm.type === AlarmNotificationTypeEnum.ZTimesYMinutesApart && tierForm.repeatCountMinutes !== undefined && tierForm.repeatZCount) ? true : false
        );

    const notificationsValid = tierForm.useScheduledNotifications ? 
                                tierForm.schedules.find(o => o.id !== ALWAYS_SCHEDULE) !== undefined :
                                tierForm.notificationLists.find(o => o.selected) !== undefined;
                                


    const stepDefinitions: StepDefinition[] = useMemo(() => {

        return [{
            key: StepEnum.Settings,
            title: t("alarmSchemeAdmin.tiers.steps.settings"),
            valid: settingsValid

        }, {
            key: StepEnum.Notifications,
            title: t("alarmSchemeAdmin.tiers.steps.notifications"),
            disabled: !settingsValid,
            valid: notificationsValid,
        }];
    }, [
        t,
        settingsValid,
        notificationsValid
    ]);

    const {
        state: {
            landing,
            steps,
            activeStep,
            cancelButtonState,
            prevButtonState,
            nextButtonState,
            saveChangesButtonState,
        },
        setActiveStep,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
    } = useStepMenuState(
        StepMenuEditModeEnum.Create, 
        undefined,
        stepDefinitions
    );

    const updateButtonStates = useCallback(() => {

         switch (activeStep) {
            case StepEnum.Settings:
                setSaveChangesButtonLabel(t(mode === TierAdminMode.Add ? 
                                            'alarmSchemeAdmin.tiers.buttons.create' : 
                                            'alarmSchemeAdmin.tiers.buttons.save'
                                            ));
                setNextButtonDisabled(!settingsValid);
                break;

            case StepEnum.Notifications:

                setSaveChangesButtonVisible(true);
                setSaveChangesButtonDisabled(!hasChanges);
                break;

            default:
                break;
        }
    }, [
        t,
        mode,
        setNextButtonDisabled,
        setSaveChangesButtonLabel,
        setSaveChangesButtonDisabled,
        setSaveChangesButtonVisible,
        activeStep,
        settingsValid,
        //propertiesValid,
        hasChanges
    ]);


    async function onCancelEdit() {

        if (!hasChanges || (await getUserExitConfirmation())) {
            onClose({ 
                reason: "cancel", 
                values: {
                    tier: tierForm
                }
            });

            reset({...defaultEditTier});
        }

        setActiveStep(StepEnum.Settings);
    }

    function onCancelStep() {
        onCancelEdit();
    }

    function onStepChange(fromStep: number, toStep: number) {

        setActiveStep(toStep);
    }

    async function onSaveChanges() {

        const values = getValues();


        setActiveStep(StepEnum.Settings);

        onClose({ 
            reason: "save", 
            values: {
                tier: values
            }
        });

        reset({...defaultEditTier});
    }

    function onExecuteAction(menuItem: ActionMenuItem) {

        switch(activeStep) {
            case StepEnum.Notifications: {
                const action = menuItem.action as TierNotificationActions | BaseActions;
                tierNotificationsRef.current?.executeAction(action);
            }
                break;

            default:
                break;
        }

    }

    async function getUserExitConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),
            confirmationText: t("cancelConfirmation.confirmationText"),
            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true,
            showCloseButton: true
        })) === "yes";
    }

    useEffect(() => {

        if (values?.item && referenceData) {
            reset(TierEditorUtils.mapToForm(values.item, referenceData, values.tiers), {keepDirtyValues: true});
        }
    }, [
        values?.item,
        values?.tiers,
        referenceData,
        reset,
    ]);



    useEffect(() => {

         updateButtonStates();
    }, [
        tierForm, 
        updateButtonStates
    ]);

    return (
        <FullScreenDialog open={open} onClose={reason => onClose({ reason: reason })}>

        <LoadingZone isLoading={referenceStatus === 'loading'}>
            
            {values?.item && <Form form={form} sx={{ padding: 0 }}>

                {isPhone && 
                <MobileStepMenu 
                    steps={steps}
                    title={mode === TierAdminMode.Add ? 'New Tier': tierForm.name}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                    viewOptionState={activeStep === StepEnum.Settings ? ViewOptionStateEnum.Hidden : viewOptionState}
                    setViewOptionState={setViewOptionState}
                    onToggleOptionsAction={() => setOpenSideSheet(!openSideSheet)}
                    actions={activeStep === StepEnum.Notifications ? 
                            !tierForm.useScheduledNotifications ?
                            tierNotificationActionItems.filter(o => o.action === TierNotificationActions.Add) : 
                                availableSchedules.length > 0 ? 
                                    tierNotificationActionItems.filter(o => o.action === TierNotificationActions.AddScheduledNotification) :
                                undefined : 
                            undefined
                    }
                    onExecuteAction={onExecuteAction}
                >
                    {activeStep === StepEnum.Settings &&
                        <TierSettings />
                    }

                    {activeStep === StepEnum.Notifications &&
                        <TierNotifications 
                            ref={tierNotificationsRef}
                            groupID={values?.groupID ?? -1}
                            organizationID={values?.organizationID ?? -1}
                            viewOptionState={viewOptionState}
                            setViewOptionState={setViewOptionState}
                            openSideSheet={openSideSheet}
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                         />
                    }


                </MobileStepMenu>
                }


                {!isPhone && <StepMenu
                    landing={landing}
                    steps={steps}
                    title={mode === TierAdminMode.Add ? 'New Tier': tierForm.name}
                    nonLinear={true}
                    activeStep={activeStep}
                    cancelButtonState={cancelButtonState}
                    prevButtonState={prevButtonState}
                    nextButtonState={nextButtonState}
                    saveChangesButtonState={saveChangesButtonState}
                    onCancel={onCancelEdit}
                    //onSaveAsNew={handleSubmit(() => {} /*onSaveAsNew()*/)}
                    onSaveChanges={handleSubmit(() => onSaveChanges())}
                    onCancelStep={onCancelStep}
                    onStepChange={onStepChange}
                    onStepClick={step => setActiveStep(step.key)}
                >
                    {activeStep === StepEnum.Settings &&
                        <TierSettings />
                    }

                    {activeStep === StepEnum.Notifications &&
                        <TierNotifications 
                            groupID={values.groupID} 
                            organizationID={values.organizationID}
                            viewOptionState={viewOptionState}
                            setViewOptionState={setViewOptionState}
                            openSideSheet={openSideSheet}
                            onCloseSideSheet={() => setOpenSideSheet(false)}
                            />
                    }

                </StepMenu>
                }
            </Form>
            }


            <UnsavedChangesPrompt
                hasChanges={hasChanges}
                getUserConfirmation={getUserExitConfirmation}
            />
        </LoadingZone>

        </FullScreenDialog>
    );
}

export type TierEditorDialogState = DialogState<TierEditorDialogInitValues, TierEditorDialogResult>;
export type BindTierEditorDialogReturn = BindDialogReturn<TierEditorDialogInitValues, TierEditorDialogResult>;

export function useTierEditorDialogState(): TierEditorDialogState {
    return useDialogState();
}

export function bindTierEditorDialog(props: TierEditorDialogState): BindTierEditorDialogReturn {
    return bindDialog(props);
}
